import request from '@/utils/api/request';
import router from '@/router';
import tenderVersionIdsQueryParam from '@/utils/formatting/tender-version-ids-query-param';

export const state = () => ({
  tenderInfo: null,
  tenderOffers: null,
  tenderOffersLoading: true,
  tenderCompanies: [],
  tenderCompaniesLoading: true,
  offersWithVersions: [],
  tenderLists: [],
  tenderListsInfoLoading: false,
  tenderBreakdownRows: {},
  showOldAndArchivedOffers: true,
  selectedVersions: {},
  refreshTenderTableTrigger: false,
});

export const mutations = {
  tenderTableRefresher(state) {
    state.refreshTenderTableTrigger = !state.refreshTenderTableTrigger;
  },
  changeTender(state, { key, value }) {
    state[key] = value;
  },
  setTenderInfo(state, tenderInfo) {
    state.tenderInfo = tenderInfo;
  },
  setTenderCompaniesLoading(state, status) {
    state.tenderCompaniesLoading = status;
  },
  setTenderOffersLoading(state, status) {
    state.tenderOffersLoading = status;
  },
  clearTenderInfo(state) {
    state.tenderInfo = null;
    state.tenderOffers = null;
    state.tenderCompanies = [];
    state.tenderListInfo = {};
    state.showOldAndArchivedOffers = true;
    state.selectedVersions = {};
  },
  changeSelectedVersion(state, { version, offerId }) {
    state.selectedVersions[offerId] = version;
  },
  insertUpdatedRow(state, { row, sums }) {
    const listId = router.currentRoute.params.tenderListId;
    const listIndex = state.tenderLists.findIndex((list) => +list.list.id === +listId);
    state.tenderLists[listIndex].project_rows_sums = sums;
    const rowIndex = state.tenderLists[listIndex].rows.findIndex(
      (localRow) => +localRow?.id === +row?.id
    );
    // Обновить все новые данные, не удаляя старые (сохранить загруженные ранее брейкдауны) при
    // установке кастомной цены родительской строки
    state.tenderLists[listIndex].rows[rowIndex] = {
      ...state.tenderLists[listIndex].rows[rowIndex],
      ...row,
    };
  },
};

export const actions = {
  async loadTenderInfo({ commit }, id) {
    try {
      const { data } = await request.get(`/tenders/${id}/`);
      commit('setTenderInfo', data);
      commit('changeTender', {
        key: 'offersWithVersions',
        value: data.offers,
      });
      let selectedVersions = {};
      data.offers.forEach((offer) => {
        selectedVersions[offer.id] = {
          ...offer.versions[offer.versions.length - 1],
          currency: offer.currency,
        };
      });
      commit('changeTender', {
        key: 'selectedVersions',
        value: selectedVersions,
      });
    } catch (e) {
      console.error(e); // eslint-disable-line
    }
  },
  async loadTenderListsInfo({ commit }, { tenderId, versionIds = [] }) {
    commit('changeTender', { key: 'tenderListsInfoLoading', value: true });
    try {
      let tenderRowsUrl = tenderVersionIdsQueryParam(`/tenders/${tenderId}/rows/`, versionIds);
      const { data } = await request.get(tenderRowsUrl);
      commit('changeTender', {
        key: 'tenderLists',
        value: data.tender_lists,
      });
    } catch (e) {
      console.error(e); // eslint-disable-line
    }
    commit('changeTender', { key: 'tenderListsInfoLoading', value: false });
  },
  async loadRowBreakdownRows({ state }, { row, selectedVersionIds }) {
    try {
      let url = `/tenders/${state.tenderInfo.id}/breakdown_rows/`;
      if (selectedVersionIds?.length) {
        let tenderVersionQuery = selectedVersionIds.map((elem) => `version_ids=${elem}`).join('&');
        url += `?${tenderVersionQuery}`;
      }
      const config = { params: { tender_row_id: row.id } };
      const { data } = await request.get(url, config);
      row.breakdownRows = data.offers;
    } catch (e) {
      console.error(e);
    }
  },
  async loadTenderOffers({ commit }, tenderId) {
    commit('setTenderOffersLoading', true);
    try {
      const { data } = await request.get(`tenders/${tenderId}/summary/`);
      commit('changeTender', { key: 'tenderOffers', value: data });
    } catch (e) {
      console.error(e);
    }
    commit('setTenderOffersLoading', false);
  },
  async loadTenderCompanies({ commit }) {
    commit('setTenderCompaniesLoading', true);
    try {
      const tenderId = router.currentRoute.params.tenderId;
      let { data } = await request.get(`/tenders/${tenderId}/offers/`);
      commit('changeTender', { key: 'tenderCompanies', value: data.results });
    } catch (e) {
      console.error(e); // eslint-disable-line
    }
    commit('setTenderCompaniesLoading', false);
  },
  async copyAllPricesToProject({ dispatch }, { versionId, versionIds = [] }) {
    try {
      const tenderId = router.currentRoute.params.tenderId;
      const listId = router.currentRoute.params.tenderListId;
      const requestUrl = tenderVersionIdsQueryParam(
        `/tenders/${tenderId}/rows/copy_all_prices_to_project/`,
        versionIds
      );
      await request.post(requestUrl, {
        tender_offer_version_id: versionId,
        list_id: listId,
      });
      await dispatch('loadTenderListsInfo', { tenderId, versionIds });
    } catch (e) {
      console.error(e); // eslint-disable-line
    }
  },
  async copyCellPricesToProject({ commit }, { versionId, column, projectRowId, versionIds = [] }) {
    try {
      const tenderId = router.currentRoute.params.tenderId;
      const listId = router.currentRoute.params.tenderListId;
      const requestUrl = tenderVersionIdsQueryParam(
        `/tenders/${tenderId}/rows/copy_cell_prices_to_project/`,
        versionIds
      );
      const { data } = await request.post(requestUrl, {
        tender_offer_version_id: versionId,
        list_id: listId,
        column,
        project_row_id: projectRowId,
      });
      await commit('insertUpdatedRow', { row: data.tender_row, sums: data.project_rows_sums });
    } catch (e) {
      console.error(e);
    }
  },
  async copyRowPricesToProject({ commit }, { versionId, projectRowId, versionIds = [] }) {
    try {
      const tenderId = router.currentRoute.params.tenderId;
      const listId = router.currentRoute.params.tenderListId;
      const requestUrl = tenderVersionIdsQueryParam(
        `/tenders/${tenderId}/rows/copy_row_prices_to_project/`,
        versionIds
      );
      const { data } = await request.post(requestUrl, {
        tender_offer_version_id: versionId,
        list_id: listId,
        project_row_id: projectRowId,
      });
      await commit('insertUpdatedRow', { row: data.tender_row, sums: data.project_rows_sums });
    } catch (e) {
      console.error(e);
    }
  },
  async editGeneralEstimateValue({ commit }, { cellInfo, versionIds }) {
    try {
      const tenderId = router.currentRoute.params.tenderId;
      const requestUrl = tenderVersionIdsQueryParam(
        `/tenders/${tenderId}/rows/${cellInfo.rowId}/`,
        versionIds
      );
      const { data } = await request.patch(requestUrl, {
        [cellInfo.key]: cellInfo.value,
      });
      await commit('insertUpdatedRow', { row: data.tender_row, sums: data.project_rows_sums });
    } catch (e) {
      console.error(e);
      commit('tenderTableRefresher');
    }
  },
};

export default { namespaced: true, state, mutations, actions };
